/**
 * MarqueeStrip — infinite-scroll text ticker.
 * Service names, accolades, or brand values separated by a primary-color dot.
 * Uses CSS animation; no GSAP required for an infinite loop.
 *
 * Registered as: custom / marquee-strip
 */
import React from "react";
import type { SectionComponentProps } from "../../registry.js";

interface MarqueeStripProps {
  items?: string[];
  /** Animation duration in seconds (default: 28) */
  speed?: number;
  /** Background override; defaults to var(--color-primary) */
  bg?: "primary" | "muted" | "background";
}

const DEFAULT_ITEMS = [
  "Licensed & Insured",
  "5-Star Rated",
  "Owner Operated",
  "Free Estimates",
  "Satisfaction Guaranteed",
];

const BG_MAP: Record<NonNullable<MarqueeStripProps["bg"]>, string> = {
  primary: "var(--color-primary)",
  muted: "var(--color-muted, hsl(var(--muted)))",
  background: "var(--color-background, hsl(var(--background)))",
};

const TEXT_MAP: Record<NonNullable<MarqueeStripProps["bg"]>, string> = {
  primary: "var(--color-primary-foreground, #fff)",
  muted: "var(--color-muted-foreground, currentColor)",
  background: "currentColor",
};

export function MarqueeStrip({ props }: SectionComponentProps) {
  const {
    items = DEFAULT_ITEMS,
    speed = 28,
    bg = "primary",
  } = props as MarqueeStripProps;

  // Duplicate items so the loop is seamless
  const track = [...items, ...items];

  const bgColor = BG_MAP[bg];
  const textColor = TEXT_MAP[bg];

  return (
    <section
      className="overflow-hidden py-3.5"
      style={{ background: bgColor }}
      id="marquee"
      aria-hidden="true"
    >
      <style>{`
        @keyframes g8-marquee { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        .g8-marquee-track { animation: g8-marquee ${speed}s linear infinite; }
        @media (prefers-reduced-motion: reduce) { .g8-marquee-track { animation-play-state: paused; } }
      `}</style>

      <div className="g8-marquee-track flex whitespace-nowrap will-change-transform">
        {track.map((item, i) => (
          <span key={i} className="inline-flex items-center gap-4 px-6 text-sm font-bold uppercase tracking-widest" style={{ color: textColor }}>
            {item}
            <span
              className="w-1.5 h-1.5 rounded-full flex-shrink-0"
              style={{ background: bg === "primary" ? "rgba(255,255,255,0.45)" : "var(--color-primary)" }}
            />
          </span>
        ))}
      </div>
    </section>
  );
}
